/**
 * Capture Lifecycle — shared timer management for media capture hooks.
 *
 * useCamera and useScreenShare both manage setInterval timers with the same
 * pattern: ref-backed timer ID, start/stop/cleanup in effects, frame counting.
 * This module extracts that into a reusable class with cleanup guarantees.
 *
 * Key guarantees:
 * - `stop()` is idempotent — safe to call multiple times
 * - `start()` clears any previous timer before starting a new one
 * - `dispose()` is a final cleanup for unmount
 */

import { logger } from '../../lib/logger-client.js'

export interface CaptureLifecycleConfig {
  /** Label for log messages (e.g. '📷 useCamera') */
  label: string
  /** Interval between captures in milliseconds */
  intervalMs: number
  /** Callback invoked on each tick */
  onTick: () => void
  /** Log level for normal operation messages (default: 'debug') */
  logLevel?: 'debug' | 'info' | 'warn'
  /** Whether to fire an immediate capture on start (default: true) */
  fireImmediately?: boolean
}

export interface CaptureLifecycleState {
  /** Whether the timer is currently running */
  isRunning: boolean
  /** Number of frames captured since start */
  frameCount: number
  /** Timestamp of last frame capture */
  lastFrameAt: number | null
  /** Timestamp when timer was started */
  startedAt: number | null
}

export class CaptureLifecycle {
  private timerId: number | null = null
  private readonly label: string
  private readonly intervalMs: number
  private readonly onTick: () => void
  private readonly logLevel: 'debug' | 'info' | 'warn'
  private readonly fireImmediately: boolean
  private frameCount = 0
  private startedAt: number | null = null
  private lastFrameAt: number | null = null
  private disposed = false

  /** Whether an async tick is currently in-flight */
  private isExecuting = false

  constructor(config: CaptureLifecycleConfig) {
    this.label = config.label
    this.intervalMs = config.intervalMs
    this.onTick = config.onTick
    this.logLevel = config.logLevel ?? 'debug'
    this.fireImmediately = config.fireImmediately ?? true
  }

  /** Start the capture timer. Clears any previous timer first. */
  start(intervalMs?: number): void {
    if (this.disposed) {
      logger.debug(`${this.label} [lifecycle] Ignoring start — disposed`)
      return
    }

    // Clear existing timer if running
    this.clearTimer()

    const ms = intervalMs ?? this.intervalMs
    this.frameCount = 0
    this.startedAt = Date.now()
    this.lastFrameAt = null

    const logFn = logger[this.logLevel].bind(logger)
    logFn(`${this.label} [lifecycle] Starting capture every ${ms}ms`)

    // Fire immediate capture
    if (this.fireImmediately) {
      this.executeTick()
    }

    this.timerId = window.setInterval(() => {
      this.executeTick()
    }, ms)
  }

  /** Restart with a new interval (preserves state). */
  restart(intervalMs?: number): void {
    if (this.disposed) return
    const ms = intervalMs ?? this.intervalMs
    this.clearTimer()

    logger.debug(`${this.label} [lifecycle] Restarting capture every ${ms}ms`, {
      framesCaptured: this.frameCount,
    })

    this.timerId = window.setInterval(() => {
      this.executeTick()
    }, ms)
  }

  /** Stop the capture timer. Idempotent. */
  stop(): void {
    if (this.timerId !== null) {
      this.clearTimer()
      logger.debug(`${this.label} [lifecycle] Stopped capture`, {
        framesCaptured: this.frameCount,
        runDurationMs: this.startedAt ? Date.now() - this.startedAt : null,
      })
    }
    this.frameCount = 0
    this.startedAt = null
    this.lastFrameAt = null
  }

  /** Final cleanup for unmount. Prevents further starts. */
  dispose(): void {
    this.disposed = true
    this.clearTimer()
    this.frameCount = 0
    this.startedAt = null
    this.lastFrameAt = null
  }

  /** Current snapshot of lifecycle state. */
  getState(): CaptureLifecycleState {
    return {
      isRunning: this.timerId !== null,
      frameCount: this.frameCount,
      lastFrameAt: this.lastFrameAt,
      startedAt: this.startedAt,
    }
  }

  /** Whether the timer is currently running. */
  get isRunning(): boolean {
    return this.timerId !== null
  }

  private executeTick(): void {
    // SEMAPHORE: skip this tick if previous onTick still in-flight.
    // Prevents overlapping async captures that cause frame buffer pressure
    // and cascading restart loops under high load.
    if (this.isExecuting) {
      logger.debug(`${this.label} [lifecycle] Skipping tick — previous capture still in-flight`)
      return
    }
    this.isExecuting = true
    try {
      this.frameCount++
      this.lastFrameAt = Date.now()
      this.onTick()
    } finally {
      this.isExecuting = false
    }
  }

  private clearTimer(): void {
    if (this.timerId !== null) {
      clearInterval(this.timerId)
      this.timerId = null
    }
  }
}

/**
 * Create a CaptureLifecycle instance.
 *
 * Convenience factory — same as `new CaptureLifecycle(config)`.
 */
export function createCaptureLifecycle(config: CaptureLifecycleConfig): CaptureLifecycle {
  return new CaptureLifecycle(config)
}